import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const Destinations = () => {
  const navigate = useNavigate();

  const destinationCategories = [
    {
      title: "Sacred Cities",
      description: "Spiritual destinations with ancient temples and religious significance",
      places: [
        {
          name: "Ujjain",
          description: "Sacred city hosting Kumbh Mela, home to Mahakaleshwar Jyotirlinga",
          path: "/destinations/ujjain",
          highlights: ["Mahakaleshwar Temple", "Kal Bhairav Temple", "Kumbh Mela", "Sacred Ghats"]
        }
      ],
      icon: "🕉️"
    },
    {
      title: "Royal Heritage",
      description: "Cities showcasing royal palaces and architectural grandeur",
      places: [
        {
          name: "Indore",
          description: "Commercial capital with magnificent palaces and rich cultural heritage",
          path: "/destinations/indore",
          highlights: ["Rajwada Palace", "Lal Bagh Palace", "Central Museum", "Royal Architecture"]
        }
      ],
      icon: "👑"
    },
    {
      title: "Cultural Experiences",
      description: "Immersive cultural destinations with local traditions",
      places: [
        {
          name: "Indore Food Culture",
          description: "Famous for street food and culinary traditions",
          path: "/destinations/indore",
          highlights: ["Sarafa Bazaar", "Local Cuisine", "Food Tours", "Night Markets"]
        }
      ],
      icon: "🎭"
    },
    {
      title: "Nature & Wildlife",
      description: "Natural attractions and scenic destinations",
      places: [
        {
          name: "Indore Nature Spots",
          description: "Beautiful dams, parks and natural attractions",
          path: "/destinations/indore",
          highlights: ["Choral Dam", "Parks", "Nature Photography", "Scenic Views"]
        }
      ],
      icon: "🌿"
    }
  ];

  const popularAttractions = [
    { name: "Mahakaleshwar Temple", path: "/destinations/ujjain/mahakaleshwar-temple", type: "Sacred" },
    { name: "Rajwada Palace", path: "/destinations/indore/rajwada-palace", type: "Heritage" },
    { name: "Sarafa Bazaar", path: "/destinations/indore/sarafa-bazaar", type: "Cultural" },
    { name: "Lal Bagh Palace", path: "/destinations/indore/lal-bagh-palace", type: "Heritage" },
    { name: "Kal Bhairav Temple", path: "/destinations/ujjain/kal-bhairav-temple", type: "Sacred" },
    { name: "Kanch Mandir", path: "/destinations/indore/kanch-mandir", type: "Heritage" },
    { name: "Central Museum", path: "/destinations/indore/central-museum", type: "Cultural" },
    { name: "Choral Dam", path: "/destinations/indore/choral-dam", type: "Nature" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Destinations - Tourist Places in Madhya Pradesh | Aao Ghumen</title>
        <meta 
          name="description" 
          content="Explore top tourist destinations in Madhya Pradesh including sacred Ujjain, royal Indore, heritage sites, cultural experiences, and natural attractions. Plan your perfect trip with expert guides."
        />
        <meta 
          name="keywords" 
          content="destinations, tourist places, Madhya Pradesh, Ujjain, Indore, temples, palaces, heritage sites, cultural tourism, nature tourism"
        />
        <link rel="canonical" href="https://aaoghumen.com/destinations" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Destinations - Tourist Places in Madhya Pradesh | Aao Ghumen" />
        <meta property="og:description" content="Discover amazing tourist destinations in Madhya Pradesh with our comprehensive travel guide." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aaoghumen.com/destinations" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Destinations - Tourist Places in Madhya Pradesh" />
        <meta name="twitter:description" content="Discover amazing tourist destinations in Madhya Pradesh with our comprehensive travel guide." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            "name": "Aao Ghumen Destinations",
            "description": "Tourist destinations and travel guide for Madhya Pradesh",
            "url": "https://aaoghumen.com/destinations",
            "areaServed": "Madhya Pradesh, India",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Tourist Destinations",
              "itemListElement": destinationCategories.map(category => ({
                "@type": "Offer",
                "name": category.title,
                "description": category.description
              }))
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-orange-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Explore Destinations
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Discover the heart of India through Madhya Pradesh's most captivating destinations
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium">
              Sacred Cities
            </span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium">
              Royal Heritage
            </span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium">
              Cultural Experiences
            </span>
            <span className="bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-medium">
              Natural Beauty
            </span>
          </div>
        </div>
      </div>

      {/* Quick Access */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 -mt-10 relative z-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Access</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <button
              onClick={() => navigate('/destinations/indore')}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-xl hover:shadow-lg transition-all"
            >
              <div className="text-3xl mb-2">🏛️</div>
              <h3 className="text-xl font-bold">Indore</h3>
              <p className="text-sm opacity-90">Royal Heritage & Culture</p>
            </button>
            <button
              onClick={() => navigate('/destinations/ujjain')}
              className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-6 rounded-xl hover:shadow-lg transition-all"
            >
              <div className="text-3xl mb-2">🕉️</div>
              <h3 className="text-xl font-bold">Ujjain</h3>
              <p className="text-sm opacity-90">Sacred Temples & Spirituality</p>
            </button>
            <button
              onClick={() => navigate('/places')}
              className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-6 rounded-xl hover:shadow-lg transition-all"
            >
              <div className="text-3xl mb-2">📍</div>
              <h3 className="text-xl font-bold">All Places</h3>
              <p className="text-sm opacity-90">Complete Directory</p>
            </button>
          </div>
        </div>
      </div>

      {/* Destination Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
          Destination Categories
        </h2>
        <div className="space-y-12">
          {destinationCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">{category.icon}</div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{category.title}</h3>
                    <p className="text-gray-600">{category.description}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6">
                  {category.places.map((place, placeIndex) => (
                    <div
                      key={placeIndex}
                      className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer hover:border-orange-300"
                      onClick={() => navigate(place.path)}
                    >
                      <h4 className="text-xl font-bold text-gray-900 mb-3">{place.name}</h4>
                      <p className="text-gray-600 mb-4">{place.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {place.highlights.map((highlight, highlightIndex) => (
                          <span
                            key={highlightIndex}
                            className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center text-orange-600 font-semibold">
                        <span>Explore {place.name}</span>
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Popular Attractions */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Popular Attractions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularAttractions.map((attraction, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer border border-gray-200 hover:border-orange-300"
                onClick={() => navigate(attraction.path)}
              >
                <div className="mb-3">
                  <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                    {attraction.type}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{attraction.name}</h3>
                <div className="flex items-center text-orange-600 font-semibold text-sm">
                  <span>Visit Now</span>
                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Planning Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl text-white p-8 md:p-12 text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-6">Ready to Start Your Journey?</h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let our expert guides help you plan the perfect trip to these incredible destinations
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/onboarding')}
              className="bg-white text-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Plan Your Trip
            </button>
            <button
              onClick={() => navigate('/places')}
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-orange-600 transition-colors"
            >
              Browse All Places
            </button>
          </div>
        </div>

        {/* SEO Content */}
        <div className="mt-16 prose prose-lg max-w-none">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Choose These Destinations?</h2>
          <div className="grid md:grid-cols-2 gap-8 text-gray-700">
            <div>
              <h3 className="text-xl font-semibold mb-4">Rich Historical Heritage</h3>
              <p className="mb-6">
                Madhya Pradesh houses some of India's most significant historical and cultural treasures. 
                From ancient temples dating back thousands of years to magnificent royal palaces, 
                each destination tells a unique story of India's glorious past.
              </p>
              
              <h3 className="text-xl font-semibold mb-4">Spiritual Significance</h3>
              <p>
                Experience the spiritual heart of India in destinations like Ujjain, one of the holiest cities 
                in Hinduism. The sacred atmosphere and ancient rituals provide a transformative 
                experience for visitors seeking spiritual enrichment.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Diverse Experiences</h3>
              <p className="mb-6">
                Whether you're interested in architecture, spirituality, culture, or nature, 
                these destinations offer something for every type of traveler. 
                From temple visits to food tours, each experience is carefully curated.
              </p>
              
              <h3 className="text-xl font-semibold mb-4">Expert Guidance</h3>
              <p>
                Our local guides provide deep insights into the history, culture, and significance 
                of each destination, ensuring you gain a comprehensive understanding of the places you visit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Destinations;